/* eslint-disable import/prefer-default-export */
import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, message } from 'antd';
import { MSG_ERROR } from '../../../../../constants';

import './RemoveAuthenticatorModal.scss';

/**
 * Confirms TOTP removal via API (password + current app code). Parent refreshes profile / form on success.
 */
export function RemoveAuthenticatorModal({
  visible,
  userRequest,
  appLabel = 'your authenticator',
  nextLabel = 'email',
  nextIsAuthenticator = false,
  onClose,
  onSuccess,
  disableAuthenticator,
}) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setPassword('');
    setCode('');
    setSubmitting(false);
  }, [visible]);

  const handleSubmit = async () => {
    const trimmedCode = `${code}`.replace(/\s/g, '');
    if (!`${password}`.trim()) {
      message.warning('Enter your account password');
      return;
    }
    if (trimmedCode.length < 6) {
      message.warning('Enter the 6-digit code from your authenticator app');
      return;
    }
    setSubmitting(true);
    try {
      await disableAuthenticator({ password, code: trimmedCode });
      await onSuccess();
      message.success(
        nextIsAuthenticator
          ? `${appLabel} has been removed. Set up ${nextLabel} before you save.`
          : `${appLabel} has been removed. Sign-in will use ${nextLabel}.`,
      );
      onClose();
    } catch (err) {
      message.error(typeof err === 'string' ? err : MSG_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Remove ${appLabel}`}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
      wrapClassName="remove-authenticator-modal-wrap"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="remove" type="danger" loading={submitting} disabled={userRequest} onClick={handleSubmit}>
          Remove app
        </Button>,
      ]}
      width="min(440px, 92vw)"
    >
      <p className="remove-authenticator-modal-lead">
        {`Enter your account password and the current code from ${appLabel}. ${
          nextIsAuthenticator
            ? `After removal, set up ${nextLabel} before you save.`
            : `After removal, sign-in will use ${nextLabel}.`
        }`}
      </p>
      <div className="remove-authenticator-modal-field">
        <span className="remove-authenticator-modal-label">Password</span>
        <Input.Password
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your password"
          autoComplete="current-password"
          aria-label="Account password"
          disabled={userRequest || submitting}
        />
      </div>
      <div className="remove-authenticator-modal-field">
        <span className="remove-authenticator-modal-label">Authenticator code</span>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
          placeholder="000000"
          maxLength={8}
          autoComplete="one-time-code"
          inputMode="numeric"
          aria-label="Authenticator app code"
          disabled={userRequest || submitting}
        />
      </div>
    </Modal>
  );
}
